import { REPORT_TYPE } from "@/app/report/common/constants"

// Custom Table 의 그룹 → 표시 컬럼 정의 (순수 함수·상수).
// 리포트 표(useCustomTableData)·설정 화면(고정 컬럼 후보)·Stats 대치(legacyStatsSection)가 같은 정의를 쓴다.
// 훅 모듈과 분리한 이유: legacyStatsSection ↔ useReportQuery ↔ useCustomTableData 순환 의존을 피하기 위함.

export const GROUP_KEY_MAPPING: Record<string, { columnKey: string; info: string }> = {
  activity: { columnKey: "activityName", info: "activityStartDate" },
  activityType: { columnKey: "activityType", info: "activityCount" },
  matchDay: { columnKey: "matchDay", info: "activityCount" },
  seasonWeek: { columnKey: "seasonWeek", info: "activityCount" },
  session: { columnKey: "sessionName", info: "sessionStartDate" },
  sessionType: { columnKey: "sessionType", info: "sessionCount" },
  // player 는 Stats by Player 를 대체하는 그룹(#99) — 표시 컬럼은 PLAYER_GROUP_COLUMNS 가 우선한다.
  // columnKey/info 는 기존 저장값(fixedColumns)·외부 사용처 호환용으로 남긴다.
  player: { columnKey: "nickname", info: "uniformNumber" },
  position1: { columnKey: "position1", info: "playerCount" },
  position2: { columnKey: "position2", info: "playerCount" },
  playerGroup: { columnKey: "playerGroup", info: "playerCount" },
  date: { columnKey: "date", info: "" },
  week: { columnKey: "week", info: "" },
  month: { columnKey: "month", info: "" },
  year: { columnKey: "year", info: "" },
}

// 선수 그룹의 표시 컬럼 — 기존 Stats by Player 와 같은 순서(등번호·이름·포지션). Trend 는 앞에 참여 활동 수.
export const PLAYER_GROUP_COLUMNS = ["uniformNumber", "nickname", "position"]
export const TREND_PLAYER_GROUP_COLUMNS = ["activityCount", ...PLAYER_GROUP_COLUMNS]
// 액티비티 그룹의 표시 컬럼 — 기존 Stats by Activity 와 같은 구성(날짜·타입·매치데이·이름·인원) (#99, Stats by Activity 대체)
export const ACTIVITY_GROUP_COLUMNS = [
  "activityStartDate",
  "activityType",
  "matchDay",
  "activityName",
  "playerCount",
]

/** 여러 컬럼을 펼치는 그룹인지 — 이런 그룹은 columnKey/info 대신 컬럼 목록을 그대로 쓴다 */
export function isMultiColumnGroup(groupKey: string | null | undefined): boolean {
  return groupKey === "player" || groupKey === "activity"
}

/** 그룹 하나가 표에 펼치는 컬럼. 선수·액티비티 그룹은 여러 컬럼이고 나머지는 columnKey 하나다. */
export function getGroupColumns(groupKey: string, reportType?: REPORT_TYPE | string): string[] {
  if (groupKey === "player") {
    return reportType === REPORT_TYPE.TREND ? TREND_PLAYER_GROUP_COLUMNS : PLAYER_GROUP_COLUMNS
  }
  if (groupKey === "activity") {
    return ACTIVITY_GROUP_COLUMNS
  }
  const group = GROUP_KEY_MAPPING[groupKey]

  return group ? [group.columnKey] : []
}

// 표시 컬럼 키 → 응답 필드. 선수 "포지션" 컬럼은 Stats 의 position(활동 데이터의 세부 포지션) = 그룹 응답의 position2.
const COLUMN_DATA_KEY: Record<string, string> = {
  position: "position2",
}

export function getColumnDataKey(column: string): string {
  return COLUMN_DATA_KEY[column] ?? column
}

// 표시 컬럼 키 → 헤더 번역 키. 선수 이름은 Stats 와 같은 "Player Name" 으로 표기한다(결정 #99-4).
const COLUMN_TITLE_KEY: Record<string, string> = {
  nickname: "playerName",
}

export function getColumnTitleKey(column: string): string {
  return COLUMN_TITLE_KEY[column] ?? column
}

/**
 * 그룹 구성에서 나오는 표시(비메트릭) 컬럼. 설정 화면의 고정 컬럼 후보와 리포트 표 헤더가 같은 함수를 써야
 * "고정할 수 있는 컬럼 = 실제 표에 있는 컬럼" 이 유지된다 (#99-8).
 * 선수·액티비티 그룹은 여러 컬럼을 펼치고, 그 외 그룹은 columnKey + (2차 그룹이 없을 때만) info 컬럼이다.
 */
export function buildGroupDisplayColumns(
  groupKey1: string | undefined,
  groupKey2: string | null | undefined,
  reportType?: REPORT_TYPE | string,
): string[] {
  const columns: string[] = []
  const group1 = groupKey1 ? GROUP_KEY_MAPPING[groupKey1] : null
  const group2 = groupKey2 ? GROUP_KEY_MAPPING[groupKey2] : null

  if (!group1 || !groupKey1) {
    return columns
  }

  columns.push(...getGroupColumns(groupKey1, reportType))
  if (group2 && groupKey2) {
    columns.push(...getGroupColumns(groupKey2, reportType))
    if (!isMultiColumnGroup(groupKey2) && group2.info) {
      columns.push(group2.info)
    }
  } else if (!isMultiColumnGroup(groupKey1) && group1.info) {
    columns.push(group1.info)
  }

  return columns.filter((column, index) => columns.indexOf(column) === index)
}
